import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { httpGet } from 'giro-react-toolkit/source/UtilityFunctions/Ajax';
import Loading from 'giro-react-toolkit/source/Loading';
import { Translation } from 'giro-react-toolkit/source/Translation';
import ClassNames from "classnames";
import { ErrorBannerContext } from 'ErrorBanner';
import { indexToMinutes } from '../utilities/CommuteTimes';
import { CanceledPromiseError, makeCancelable } from '../utilities/Promise';

import './CensusStats.scss';

/**
 * Composant qui affiche une ligne du tableau de recensement
 */
function CensusTableRow(props) {
   if (!props.current ||
      !props.future ||
      props.index >= reachMap.census.statisticTypes.length ||
      !reachMap.census.statisticTypes[props.index])
      return null;

   const variation = props.future - props.current;
   const percent = Math.round((variation / props.current) * 100);
   const percentText = ((percent === 0) ? "~0" : percent.toString());

   const classNames = ClassNames("census-stat-percent", {
      "census-stat-positive": variation >= 0,
      "census-stat-negative": variation < 0,
      "census-stat-hidden": variation == 0
   });

   return (
      <tr>
         <th>{reachMap.census.statisticTypes[props.index].Label}</th>
         <td>{props.current}</td>
         <td>{props.future}</td>
         <td className="census-stat-variation">
            <div className="census-stat-variation__container">
               <div className="census-stat-variation__quantity">
                  {variation}
               </div>
               <div>
                  <span className={classNames}>{percentText}%</span>
               </div>
            </div>
         </td>
      </tr>
   );
}

/**
 * Composant qui affiche un tableau de données de recensement
 * */
class CensusStats extends Component {
   constructor(props) {
      super(props);

      this.state = { censusData: [], loading: false };

      this.abortControllerCurrent = null;
      this.abortControllerProposed = null;
      this.cancelablePromise = null;
   }

   componentDidMount() {
      if (this.arePropsInitialized())
         this.getCensusData();
   }

   componentWillUnmount() {
      this.cancelProgressCensusDataRequest();
   }

   componentDidUpdate(prevProps) {
      // If props have changed, fetch new censusData
      // Do not fetch censusData if Location changed because this component will be unmounted if location changes
      if (this.props.isochroneParam.selectedDay !== prevProps.isochroneParam.selectedDay ||
         this.props.isochroneParam.selectedTime !== prevProps.isochroneParam.selectedTime ||
         this.props.isochroneParam.selectedDur !== prevProps.isochroneParam.selectedDur) {
         this.getCensusData();
      }
   }

   /**
    * Returns if props are initialized to get census data
    * */
   arePropsInitialized() {
      return (this.props.isochroneParam.selectedLocation.latitude &&
         this.props.isochroneParam.selectedLocation.longitude &&
         this.props.isochroneParam.selectedDay &&
         this.props.isochroneParam.selectedTime &&
         this.props.isochroneParam.selectedDur);
   }

   /**
    * Cancel the in progress census data request if any
    */
   cancelProgressCensusDataRequest() {
      if (this.abortControllerCurrent)
         this.abortControllerCurrent.abort();

      if (this.abortControllerProposed)
         this.abortControllerProposed.abort();

      if (this.cancelablePromise)
         this.cancelablePromise.cancel();
   }

   /**
    * Transforms census data from the API to a format that can be used easily in
    * this component
    * @param {object} currentScenarioData
    * @param {object} futureScenarioData
    */
   processCensusData(currentScenarioData, futureScenarioData) {

      // API returns a HttpStatusCodeResult instead of ContentResult if no data
      // HttpStatusCodeResult with no data has status code 204
      if ((currentScenarioData.status) && (currentScenarioData.status === 204))
         return ([]);

      // Merge both arrays into one with an object with current/future as the leaf
      return (
         currentScenarioData.TravelTimeRangeStats.map((timePeriod, i) => {
            return (
               timePeriod.map((data, j) => {
                  return ({ current: data, future: futureScenarioData.TravelTimeRangeStats[i][j] });
               })
            );
         })
      )
   }

   /**
    * Fetches census data from props and saves it to state
    **/
   getCensusData() {
      // Cancel the in progress census data request and make a new one.
      this.cancelProgressCensusDataRequest();

      // on doit faire 2 appels aux services, une pour le scenario courrant et un autre
      // pour le scenario future pour permettre d'afficher et comparer les deux
      var paramsCurrent = {
         latitude: this.props.isochroneParam.selectedLocation.latitude,
         longitude: this.props.isochroneParam.selectedLocation.longitude,
         day: this.props.isochroneParam.selectedDay,
         time: this.props.isochroneParam.selectedTime,
         duration: this.props.isochroneParam.selectedDur,
         scenario: reachMap.concept.scenarios[0].Value//current scenario
      }

      var paramsFuture = { ...paramsCurrent, scenario: reachMap.concept.scenarios[1].Value };//proposed scenario

      this.setState({ loading: true });

      var showError = (response) => {
         this.context.displayErrorMessage(
            "ReachMapRes.StatsFetchErrorText",
            { errorCode: response.status });
         this.setState({ censusData: [], loading: false });
      };

      if (window.AbortController) {
         this.abortControllerCurrent = new AbortController();
         this.abortControllerProposed = new AbortController();
      }

      var signalCurrent = this.abortControllerCurrent ? this.abortControllerCurrent.signal : null;
      var signalProposed = this.abortControllerProposed ? this.abortControllerProposed.signal : null;

      // On fait les 2 appels en parallèle et on attend que les 2 appels soient complétés avant d'afficher les statistiques
      this.cancelablePromise = makeCancelable(
         Promise.all([
            httpGet('home/ReachMapStats', { params: paramsCurrent, fetchOptions: { signalCurrent }, errorHandler: showError }),
            httpGet('home/ReachMapStats', { params: paramsFuture, fetchOptions: { signalProposed }, errorHandler: showError })
         ]));

      this.cancelablePromise.promise.then(
         result => {
            if (result && result.length)
               this.setState({ censusData: this.processCensusData(result[0], result[1]), loading: false });
            else
               this.setState({ censusData: [], loading: false });
         },
         reason => {
            if (!(reason instanceof CanceledPromiseError)) {
               this.setState({ censusData: [], loading: false });
               showError({ status: '[rejected]' });
            }
         }
      ).catch(
         () => {
            this.setState({ censusData: [], loading: false });
            showError({ status: '[error]' });
         }
      );
   }

   render() {
      // afficher un chargement si les données ne sont pas chargées encore
      if (this.state.loading)
         return (
            <div className="census-stats census-stats--loading">
               <Loading isDisplayed scale="small" delayed={false} />
               <div className="census-stat-loading-text">
                  <Translation resourceKey="ReachMapRes.StatsLoadingText" />
               </div>
            </div>
         );

      // si on n'a pas de données à afficher on affiche rien
      if (!this.state.censusData.length ||
         this.props.selectedTimeIndex == null ||
         this.state.censusData.length < this.props.selectedTimeIndex)
         return (null);

      const commuteTime = indexToMinutes(this.props.selectedTimeIndex, this.props.isochroneParam.selectedDur);

      return (
         <div className="census-stats">
            <div className="census-stats__title"><Translation resourceKey="ReachMapRes.StatsTitle" params={{ nbMin: commuteTime }} /></div>
            <div className="census-stats__table">
               <table>
                  <thead>
                     <tr>
                        <th>&nbsp;</th>
                        <th><Translation resourceKey="ReachMapRes.StatsCurrentTitle" /></th>
                        <th><Translation resourceKey="ReachMapRes.StatsProposedTitle" /></th>
                        <th><Translation resourceKey="ReachMapRes.StatsVariationTitle" /></th>
                     </tr>
                  </thead>
                  <tbody>
                     {this.state.censusData[this.props.selectedTimeIndex].map((r, i) => {
                        return (<CensusTableRow {...r} index={i} key={i} />)
                     })}
                  </tbody>
               </table>
            </div>
         </div>
      );
   }

}

CensusStats.contextType = ErrorBannerContext;

CensusStats.propTypes = {
   /**
    * Index (zero based) in the legend of the selected commute time
    * null when nothing is selected
    */
   selectedTimeIndex: PropTypes.number,

   /**
    * Isochrone parameters
    */
   isochroneParam: PropTypes.object
}

export default CensusStats;
